// TeacherPickupSheet — read-only sheet teachers open at the door to
// see who's authorised to collect a child. Renders photo, relationship,
// phone (tap to call), PIN-set indicator, emergency-contact pill.
//
// Always lazy-loads the list on open (cheap query, RLS-scoped).

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CheckCircle, Phone, ShieldCheck, UserCircle, House } from "@phosphor-icons/react";
import { toast } from "sonner";
import { listForChild } from "@/lib/pickupAuthorizations";
import { stampPickup, todayStr } from "@/lib/attendance";
import PickupPhoto from "@/components/pickup/PickupPhoto";
import PickupVerifyDialog from "@/components/pickup/PickupVerifyDialog";

export default function TeacherPickupSheet({
  open,
  onOpenChange,
  child, // {id, first_name, last_name}
  onPickupConfirmed,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState({ open: false, person: null });

  useEffect(() => {
    if (!open || !child?.id) return;
    let alive = true;
    setLoading(true);
    listForChild(child.id)
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch(() => {
        if (alive) setRows([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, child?.id]);

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="teacher-pickup-sheet"
        side="bottom"
        className="max-h-[85dvh] overflow-y-auto rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">
            Authorised pickup
          </SheetTitle>
          <SheetDescription>
            For{" "}
            <strong className="text-neutral-900">
              {[child?.first_name, child?.last_name].filter(Boolean).join(" ")}
            </strong>
            . Legal guardians on file are always authorised — use the
            "Parent self-pickup" button when a guardian is collecting
            their child.
          </SheetDescription>
        </SheetHeader>

        {/* Parent self-pickup — fast path for legal guardians who don't
           have a separate authorization row. Stamps with pickup_self=true
           and pickup_authorization_id=null. */}
        <button
          type="button"
          data-testid="teacher-pickup-self"
          disabled={busy || !child?.id}
          onClick={async () => {
            const childName =
              [child?.first_name, child?.last_name].filter(Boolean).join(" ") ||
              "this child";
            if (!window.confirm(`Confirm a parent/guardian is picking up ${childName}?`)) return;
            setBusy(true);
            try {
              await stampPickup({
                childId: child.id,
                date: todayStr(),
                pickupSelf: true,
              });
              toast.success("Pickup confirmed · parent/guardian");
              onPickupConfirmed?.({ self: true });
              onOpenChange?.(false);
            } catch (err) {
              toast.error(err.message || "Could not stamp pickup");
            } finally {
              setBusy(false);
            }
          }}
          className="mt-3 flex w-full items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-3 text-left transition-all hover:-translate-y-[1px] hover:shadow-sm disabled:opacity-60"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary-hover">
            <House size={20} weight="duotone" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-neutral-900">
              Parent self-pickup
            </span>
            <span className="block text-[12px] text-neutral-600">
              The legal guardian is collecting their own child. We'll
              record this as a self-pickup.
            </span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-hover">
            <CheckCircle size={10} weight="duotone" />
            Confirm
          </span>
        </button>

        {loading && (
          <p className="mt-4 text-sm text-neutral-500">Loading…</p>
        )}

        {!loading && rows.length === 0 && (
          <p
            data-testid="teacher-pickup-empty"
            className="mt-4 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500"
          >
            <UserCircle size={20} weight="duotone" className="mx-auto mb-1 text-neutral-400" />
            No additional pickup people on file. Only legal guardians
            may collect this child today.
          </p>
        )}

        {!loading && rows.length > 0 && (
          <ul className="mt-4 space-y-2">
            {rows.map((r) => (
              <li
                key={r.id}
                data-testid={`teacher-pickup-row-${r.id}`}
                className="rounded-2xl border border-neutral-200 bg-white"
              >
                <button
                  type="button"
                  data-testid={`teacher-pickup-select-${r.id}`}
                  onClick={() => setVerify({ open: true, person: r })}
                  className="flex w-full items-start gap-3 rounded-2xl p-3 text-left transition-all hover:-translate-y-[1px] hover:shadow-sm"
                >
                  <PickupPhoto path={r.photo_path} name={r.full_name} size={48} />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1 text-sm font-semibold text-neutral-900">
                      {r.full_name}
                      {r.is_emergency_contact && (
                        <span className="rounded-full bg-[#C6851E]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#8A5A12]">
                          Emergency
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-[12px] text-neutral-600">
                      {r.relationship || "—"}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {r.phone && (
                        <a
                          href={`tel:${r.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`teacher-pickup-call-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-700 hover:bg-neutral-50"
                        >
                          <Phone size={10} weight="duotone" />
                          {r.phone}
                        </a>
                      )}
                      {r.pin && (
                        <span
                          data-testid={`teacher-pickup-pin-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                        >
                          <ShieldCheck size={10} weight="duotone" />
                          PIN required
                        </span>
                      )}
                    </div>
                    {r.notes && (
                      <p className="mt-1 text-[11px] italic text-neutral-500">
                        "{r.notes}"
                      </p>
                    )}
                  </div>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-hover">
                    <CheckCircle size={10} weight="duotone" />
                    Confirm
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
    <PickupVerifyDialog
      open={verify.open}
      onOpenChange={(o) => setVerify((s) => ({ ...s, open: o }))}
      child={child}
      person={verify.person}
      onCompleted={(p) => {
        onPickupConfirmed?.(p);
        onOpenChange?.(false);
      }}
    />
    </>
  );
}
